import { css } from '@emotion/css';
import { sharedTheme } from 'Shared/ThemeProvider';
import { onCleanup, onMount, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';


const overlayStyle = () => css({
	width: '100vw',
	height: '100vh',
	position: 'fixed',
	top: 0,
	left: 0,
	zIndex: 10,
	pointerEvents: 'none',
});

const backgroundStyle = () => css({
	width: '100%',
	height: '100%',
	backgroundColor: 'rgba(0, 0, 0, 0.2)',
	pointerEvents: 'auto',
});

const bodyStyle = () => css(sharedTheme().root,{
	position: 'fixed',
	width: 'fit-content',
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: 'white',
	borderRadius: '0.5em',
	overflow: 'hidden',
	boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
	pointerEvents: 'auto',
});

const titleBarStyle = () => css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	backgroundColor: '#555',
	color: 'white',
	padding: '0.4em 0.6em',
	userSelect: 'none',
	gap: '1em',
});

const titleBarMoveableStyle = () => css({
	cursor: 'grab',
});

const titleBarDraggingStyle = () => css({
	cursor: 'grabbing',
});

const contentStyle = () => css({
	padding: '0.5em',
});

const closeStyle = () => css({
	fontWeight: 'bold',
	fontSize: '1.1em',
	width: '1.4em',
	height: '1.4em',
	borderRadius: '50%',
	border: 'solid 2px white',
	backgroundColor: 'transparent',
	color: 'white',
	userSelect: 'none',
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	lineHeight: 1,
	padding: 0,
	flexShrink: 0,
});


export function Popup(P: { title?: string, moveable?: boolean, close: () => void, children: any })
{
	const [pos, setPos] = createSignal({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
	const [dragging, setDragging] = createSignal(false);
	let dragOffset = { x: 0, y: 0 };
	let bodyEl: HTMLDivElement;

	function clamp(x: number, y: number) {
		const hw = bodyEl.offsetWidth / 2;
		const hh = bodyEl.offsetHeight / 2;
		return {
			x: Math.max(hw, Math.min(window.innerWidth - hw, x)),
			y: Math.max(hh, Math.min(window.innerHeight - hh, y)),
		};
	}

	function onTitleMouseDown(e: MouseEvent) {
		if (!P.moveable || (e.target as HTMLElement).closest('button')) return;
		setDragging(true);
		dragOffset = { x: e.clientX - pos().x, y: e.clientY - pos().y };
		e.preventDefault();
	}

	function onMouseMove(e: MouseEvent) {
		if (!dragging()) return;
		setPos(clamp(e.clientX - dragOffset.x, e.clientY - dragOffset.y));
	}

	function onMouseUp() {
		setDragging(false);
	}

	function onResize() {
		setPos(p => clamp(p.x, p.y));
	}

	onMount(() => {
		document.body.style.overflowY = 'hidden';
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('resize', onResize);

		onCleanup(() => {
			document.body.style.overflowY = '';
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
			window.removeEventListener('resize', onResize);
		});
	});

	const titleBarClasses = () => [
		titleBarStyle(),
		P.moveable ? titleBarMoveableStyle() : '',
		dragging() ? titleBarDraggingStyle() : '',
	].join(' ');

	return (
		<Portal>
			<div class={overlayStyle()}>
				<div class={backgroundStyle()} onClick={P.close} />
				<div
					ref={bodyEl}
					class={bodyStyle()}
					style={{ left: `${pos().x}px`, top: `${pos().y}px`, transform: 'translateX(-50%) translateY(-50%)' }}
				>
					<div class={titleBarClasses()} onMouseDown={onTitleMouseDown}>
						<span>{P.title}</span>
						<button class={closeStyle()} onClick={P.close}>×</button>
					</div>

					<div class={contentStyle()}>
						{P.children}
					</div>
				</div>
			</div>
		</Portal>
	);
}
